/**
 * StagePromptsSection (TASK-238)
 *
 * Per-stage operator prompts editor, shown on the operator page (stages tab) when the
 * user's template has `use_stage = true`. One textarea per funnel stage;
 * each saves independently via PUT /users/{id}/stage-prompts/{codename}/.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { StageBadge } from '@/components/funnel/FunnelBadges';
import { funnelApi } from '@/services/api/funnel';
import type { StagePrompt } from '@/types/funnel';

interface StagePromptsSectionProps {
  /** Whether the user's template uses per-stage prompts (`template.use_stage`). */
  useStage: boolean;
  userId: string;
}

function StagePromptRow({
  userId,
  stagePrompt,
}: {
  userId: string;
  stagePrompt: StagePrompt;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(stagePrompt.prompt);

  useEffect(() => {
    setValue(stagePrompt.prompt);
  }, [stagePrompt.prompt]);

  const mutation = useMutation({
    mutationFn: (prompt: string) =>
      funnelApi.updateStagePrompt(userId, stagePrompt.stage_codename, prompt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stage-prompts', userId] });
      toast.success(`Промпт стадии «${stagePrompt.stage_label}» сохранён`);
    },
    onError: () => toast.error('Не удалось сохранить промпт стадии'),
  });

  const isDirty = value !== stagePrompt.prompt;

  return (
    <div
      data-testid={`stage-prompt-${stagePrompt.stage_codename}`}
      className="space-y-2 rounded-md border p-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StageBadge
            stage={stagePrompt.stage_codename}
            label={stagePrompt.stage_label}
          />
          {stagePrompt.is_default && (
            <span
              data-testid={`stage-prompt-default-${stagePrompt.stage_codename}`}
              className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
            >
              по умолчанию
            </span>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`stage-prompt-save-${stagePrompt.stage_codename}`}
          disabled={!isDirty || mutation.isPending}
          onClick={() => mutation.mutate(value)}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
        </Button>
      </div>
      <Textarea
        data-testid={`stage-prompt-input-${stagePrompt.stage_codename}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          'Переопределение ЦЕЛИ и ТАКТИКИ бота для этой стадии (дерево услуг ' +
          'и инструменты остаются общими). Напр.: смета уже посчитана — НЕ ' +
          'предлагай пересчитывать, услуги теперь как справочник; цель — ' +
          'получить согласие на сделку и отработать возражения.'
        }
        rows={3}
      />
    </div>
  );
}

export function StagePromptsSection({
  useStage,
  userId,
}: StagePromptsSectionProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['stage-prompts', userId],
    queryFn: () => funnelApi.getStagePrompts(userId),
    enabled: useStage,
  });

  if (!useStage) {
    return (
      <div
        data-testid="stage-prompts-disabled"
        className="rounded-md border border-dashed p-4 text-sm text-gray-500"
      >
        Промпты по стадиям доступны для шаблонов с поддержкой воронки
        (use_stage). Включите соответствующий шаблон, чтобы настроить их.
      </div>
    );
  }

  const stagePrompts = data?.stage_prompts ?? [];

  return (
    <div data-testid="stage-prompts-section" className="space-y-3">
      <div className="space-y-1">
        <Label>Промпты по стадиям воронки</Label>
        <p data-testid="stage-prompts-hint" className="text-xs text-gray-500">
          Применяется ко ВСЕМ каналам поверх их базового или кастомного промпта.
          Переопределяет ЦЕЛЬ и ТАКТИКУ бота под текущую стадию клиента; дерево
          услуг, инструменты и расчёты — общие.
        </p>
      </div>

      {isLoading ? (
        <div
          data-testid="stage-prompts-loading"
          className="flex items-center justify-center py-8"
        >
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : stagePrompts.length === 0 ? (
        <p className="text-sm text-gray-500">Стадии не настроены</p>
      ) : (
        <div className="space-y-3">
          {[...stagePrompts]
            .sort((a, b) => a.stage_order - b.stage_order)
            .map((sp) => (
              <StagePromptRow
                key={sp.stage_codename}
                userId={userId}
                stagePrompt={sp}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default StagePromptsSection;
